import { Attachment } from 'stream-chat'
import {ChatFileTypes, GiphyAttachment} from '../types'

export const getAttachmentLabel = (attachment: Attachment) => {
  switch (attachment.type) {
    case 'file':
      return '📎'
    case 'video':
      return '🎥'
    case 'image':
      return '📷'
    case 'giphy':
      return '🖼️'
    case 'voiceRecording':
      return '🎤'
    default:
      return 'New message'
  }
}

export function formatPlaybackTime(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export const getAttachmentFallbackText = (attachment: Attachment) => {
  switch (attachment.type) {
    case 'file':
      return `📎 ${attachment.title || 'File'}`
    case 'video':
      return `🎥 ${attachment.title || 'Video'}`
    case 'image':
      return '📷 Photo'
    case 'giphy':
      return '🖼️ GIF'
    case 'voiceRecording':
      return `🎤 ${formatPlaybackTime(attachment?.duration * 1000)}`
    default:
      return attachment.title || 'New message'
  }
}

/**
 * Extensions accepted as `file` attachments in the chat composer.
 *
 * Mirrors the MIME→ext table in `getAttachmentExtension` below — i.e. the set
 * of types the message renderers can actually display — so the composer never
 * lets through something it can't render back. Images are kept here too even
 * though "Photos" has its own picker, so attaching an image via "Files" still
 * works. Anything outside this list (e.g. `.exe`, `.msi`, `.bat`) is rejected
 * before upload.
 *
 * Added for Connect Issues "Unsupported file types (e.g. .exe, Windows
 * installer) are incorrectly accepted on Web".
 */
export const ACCEPTED_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.json',
  '.xml',
  '.zip',
  '.rar',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.mp4',
  '.mov',
  '.mp3',
  '.wav',
] as const

/** Comma-separated form for a native `<input accept="...">` attribute. */
export const ACCEPTED_ATTACHMENT_ACCEPT_ATTR =
  ACCEPTED_ATTACHMENT_EXTENSIONS.join(',')

/**
 * True when `fileName`'s extension is an accepted chat `file` attachment.
 * Used as a client-side guard since the `<input accept>` filter is bypassable
 * (drag-and-drop, "all files" option in some OS pickers).
 *
 * @example isAcceptedAttachmentFile('setup.exe') // false
 */
export function isAcceptedAttachmentFile(fileName: string): boolean {
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex <= 0 || dotIndex === fileName.length - 1) return false
  const extension = fileName.slice(dotIndex).toLowerCase()
  return (ACCEPTED_ATTACHMENT_EXTENSIONS as readonly string[]).includes(
    extension,
  )
}

export function getAttachmentExtension(attachment: Attachment): string {
  if (attachment.mime_type) {
    const mimeToExt: Record<string, string> = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        '.xlsx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        '.pptx',
      'text/plain': '.txt',
      'text/csv': '.csv',
      'application/json': '.json',
      'application/xml': '.xml',
      'application/zip': '.zip',
      'application/x-rar-compressed': '.rar',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'video/mp4': '.mp4',
      'video/quicktime': '.mov',
      'audio/mpeg': '.mp3',
      'audio/wav': '.wav',
    }

    if (mimeToExt[attachment.mime_type]) {
      return mimeToExt[attachment.mime_type]
    }
  }

  const urlToCheck =
    attachment.asset_url || attachment.image_url || (attachment as any).url
  if (urlToCheck) {
    try {
      const urlWithoutQuery = urlToCheck.split('?')[0]
      const urlParts = urlWithoutQuery.split('.')
      if (urlParts.length > 1) {
        const extension = urlParts[urlParts.length - 1].toLowerCase()
        if (extension && extension.length <= 5) {
          return `.${extension}`
        }
      }

      const urlParams = new URL(urlToCheck).searchParams
      for (const [, value] of urlParams.entries()) {
        if (value.includes('.')) {
          const valueParts = value.split('.')
          if (valueParts.length > 1) {
            const extension = valueParts[valueParts.length - 1].toLowerCase()
            if (
              extension &&
              extension.length <= 5 &&
              /^[a-z0-9]+$/.test(extension)
            ) {
              return `.${extension}`
            }
          }
        }
      }
    } catch (err) {}
  }

  if (attachment.title || (attachment as any).name) {
    const fileName = attachment.title || (attachment as any).name || ''
    if (fileName && typeof fileName === 'string') {
      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex > 0 && dotIndex < fileName.length - 1) {
        return fileName.substring(dotIndex)
      }
    }
  }

  if (
    (attachment.image_url || (attachment as any).url) &&
    !attachment.asset_url
  ) {
    return '.jpg'
  }

  return '.bin'
}

const fileTypeToTextMap: Record<string, string> = {
  file: 'File',
  image: 'Image',
  video: 'Video',
  giphy: 'GIF',
  audio: 'Audio',
  voiceRecording: 'Voice message',
}

const fileTypeToIconMap: Record<string, string> = {
  file: 'file-06',
  image: 'image-01',
  video: 'video-recorder',
  giphy: 'sticker-circle',
  audio: 'microphone-01',
  voiceRecording: 'microphone-01',
}

export function attachmentIcon(attachment?: Attachment) {
  return fileTypeToIconMap[attachment?.type]
}

/**
 * Equality predicate for a stream-chat Attachment. Compares every field that
 * any renderer in `apps/mobile`'s MessageAttachments reads off the attachment
 * today:
 *
 *   <Images>   image_url, og_scrape_url, thumb_url, title_link, file_size
 *   <Audios>   asset_url, file_size, title_link
 *   <Files>    asset_url, file_size, title_link
 *   <GIFs>     custom.giphy_id, custom.aspect_ratio
 *
 * Plus `type`, which is what the renderers branch on. When adding a new
 * renderer that reads a new attachment field, extend this comparator AND the
 * list above.
 *
 * Used as the `isAttachmentEqual` prop on the mobile <ChatChannel>, which
 * `stream-chat-react-native` ANDs with its own default comparator inside its
 * Message memo. Without this, the SDK's default collapses to
 * `prev.type === next.type` for non-image attachments (see Message.tsx
 * `areEqual`), so any in-place hydration of `custom.giphy_id`, `file_size`,
 * etc. after the first render is silently dropped until a cold start.
 *
 * Originally added in PR 1431 to fix GIFs rendering blank on iOS.
 *
 * @example
 *   <ChatChannel isAttachmentEqual={singleAttachmentEqual} ... />
 */
export function singleAttachmentEqual(prev: Attachment, next: Attachment): boolean {
  const prevCustom = (prev as GiphyAttachment).custom
  const nextCustom = (next as GiphyAttachment).custom
  return (
    prev.type === next.type &&
    prev.image_url === next.image_url &&
    prev.og_scrape_url === next.og_scrape_url &&
    prev.thumb_url === next.thumb_url &&
    prev.title_link === next.title_link &&
    prev.asset_url === next.asset_url &&
    prev.file_size === next.file_size &&
    prevCustom?.giphy_id === nextCustom?.giphy_id &&
    prevCustom?.aspect_ratio === nextCustom?.aspect_ratio
  )
}

export function attachmentPreviewText(attachment?: Attachment) {
  if (!attachment) return null
  const fileType = attachment.type
  if (!fileType) return null
  const extension = getAttachmentExtension(attachment)
  const label = fileTypeToTextMap[fileType]
  if (!label) return null
  const typesWithoutExtension = [
    ChatFileTypes.Image,
    ChatFileTypes.Audio,
    ChatFileTypes.VoiceRecording,
  ]
  return typesWithoutExtension.includes(fileType as ChatFileTypes)
    ? label
    : `${label}${extension}`
}